import { type ReactNode } from "react";
import { Check } from "lucide-react";
import clsx from "clsx";

export interface StepperStep {
  id: string;
  label: ReactNode;
  description?: ReactNode;
}

export type StepperProps = {
  steps: StepperStep[];
  /** Index of the current step (0-based). Steps before it are "complete", after it "upcoming". */
  activeIndex: number;
  /** Lets the consumer allow clicking a completed step to go back — omit for a linear, non-clickable stepper. */
  onStepClick?: (index: number) => void;
  orientation?: "horizontal" | "vertical";
  className?: string;
};

// Row 21.11 — a step-indicator/wizard-nav primitive. Owns ONLY the indicator UI (numbered/
// checked circles + connecting rail + labels) — unlike Tabs.tsx (whose TabItem carries its
// own `content` and renders it), Stepper does NOT render step content. The consumer's own
// layout owns the panel and switches it by reading the same `activeIndex` this component
// renders against — e.g. `{activeIndex === 0 && <StepOnePanel />}` alongside
// `<Stepper steps={steps} activeIndex={activeIndex} onStepClick={setActiveIndex} />`.
export function Stepper({ steps, activeIndex, onStepClick, orientation = "horizontal", className }: StepperProps) {
  return (
    <ol
      className={clsx(
        "flex",
        orientation === "horizontal" ? "flex-row items-start" : "flex-col",
        className,
      )}
    >
      {steps.map((step, i) => {
        const status = i < activeIndex ? "complete" : i === activeIndex ? "active" : "upcoming";
        const clickable = Boolean(onStepClick) && status !== "upcoming";
        const isLast = i === steps.length - 1;
        return (
          <li
            key={step.id}
            className={clsx(
              "flex",
              orientation === "horizontal" ? "flex-1 flex-col items-center text-center" : "flex-row items-start gap-3",
            )}
          >
            <div className={clsx("flex items-center", orientation === "horizontal" ? "w-full" : "flex-col")}>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepClick?.(i)}
                aria-current={status === "active" ? "step" : undefined}
                className={clsx(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors",
                  status === "complete" && "bg-[var(--color-accent)] text-[var(--color-text-inverse)]",
                  status === "active" && "border-2 border-[var(--color-accent)] text-[var(--color-accent)]",
                  status === "upcoming" && "border border-[var(--color-border-default)] text-[var(--color-text-secondary)]",
                  clickable && "cursor-pointer",
                  !clickable && "cursor-default",
                )}
              >
                {status === "complete" ? <Check size={16} aria-hidden /> : i + 1}
              </button>
              {!isLast && (
                <div
                  aria-hidden
                  className={clsx(
                    orientation === "horizontal" ? "mx-2 h-px flex-1" : "my-1 w-px flex-1 self-stretch",
                    status === "complete" ? "bg-[var(--color-accent)]" : "bg-[var(--color-border-default)]",
                  )}
                />
              )}
            </div>
            <div className={clsx(orientation === "horizontal" ? "mt-2" : "pb-6")}>
              <p
                className={clsx(
                  "text-sm font-medium",
                  status === "upcoming" ? "text-[var(--color-text-secondary)]" : "text-[var(--color-text-primary)]",
                )}
              >
                {step.label}
              </p>
              {step.description && (
                <p className="text-xs text-[var(--color-text-secondary)]">{step.description}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
